import React from "react";
import PropTypes from "prop-types";
import Skeleton, { SkeletonGroup } from "./Skeleton";
import "./LoadingSystem.css";

/**
 * CommonSkeletons - Colección de skeletons predefinidos para casos de uso comunes
 *
 * Estos componentes proporcionan skeletons listos para usar que coinciden
 * con patrones de UI comunes en la aplicación.
 */

/**
 * CardSkeleton - Skeleton para cards/tarjetas
 */
export const CardSkeleton = ({
  hasImage = true,
  hasAvatar = false,
  lines = 3,
  className = "",
}) => {
  return (
    <div className={`loading-skeleton-card ${className}`}>
      {hasImage && (
        <Skeleton variant="rectangular" width="100%" height="180px" />
      )}
      <div className="loading-skeleton-card__content">
        {hasAvatar && (
          <Skeleton variant="circular" width="40px" height="40px" />
        )}
        <Skeleton variant="text" width="80%" height="20px" />
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={`${90 - index * 10}%`}
            height="16px"
          />
        ))}
      </div>
    </div>
  );
};

CardSkeleton.propTypes = {
  hasImage: PropTypes.bool,
  hasAvatar: PropTypes.bool,
  lines: PropTypes.number,
  className: PropTypes.string,
};

/**
 * TableSkeleton - Skeleton para tablas
 */
export const TableSkeleton = ({
  columns = 4,
  rows = 5,
  hasHeader = true,
  className = "",
}) => {
  return (
    <div className={`loading-skeleton-table ${className}`}>
      {hasHeader && (
        <div className="loading-skeleton-table__header">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} variant="text" width="100%" height="20px" />
          ))}
        </div>
      )}
      <div className="loading-skeleton-table__body">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="loading-skeleton-table__row">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                variant="text"
                width="100%"
                height="16px"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

TableSkeleton.propTypes = {
  columns: PropTypes.number,
  rows: PropTypes.number,
  hasHeader: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * ListSkeleton - Skeleton para listas de items
 */
export const ListSkeleton = ({
  items = 5,
  hasAvatar = false,
  hasIcon = false,
  className = "",
}) => {
  return (
    <div className={`loading-skeleton-list ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="loading-skeleton-list__item">
          {(hasAvatar || hasIcon) && (
            <Skeleton
              variant={hasAvatar ? "circular" : "rectangular"}
              width="40px"
              height="40px"
            />
          )}
          <div className="loading-skeleton-list__content">
            <Skeleton variant="text" width="70%" height="18px" />
            <Skeleton variant="text" width="90%" height="14px" />
          </div>
        </div>
      ))}
    </div>
  );
};

ListSkeleton.propTypes = {
  items: PropTypes.number,
  hasAvatar: PropTypes.bool,
  hasIcon: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * FormSkeleton - Skeleton para formularios
 */
export const FormSkeleton = ({
  fields = 4,
  hasTitle = true,
  hasButtons = true,
  className = "",
}) => {
  return (
    <div className={`loading-skeleton-form ${className}`}>
      {hasTitle && (
        <Skeleton
          variant="text"
          width="60%"
          height="28px"
          className="loading-skeleton-form__title"
        />
      )}

      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="loading-skeleton-form__field">
          <Skeleton variant="text" width="30%" height="16px" />
          <Skeleton variant="rectangular" width="100%" height="40px" />
        </div>
      ))}

      {hasButtons && (
        <div className="loading-skeleton-form__buttons">
          <Skeleton
            variant="rectangular"
            width="120px"
            height="40px"
            borderRadius="6px"
          />
          <Skeleton
            variant="rectangular"
            width="120px"
            height="40px"
            borderRadius="6px"
          />
        </div>
      )}
    </div>
  );
};

FormSkeleton.propTypes = {
  fields: PropTypes.number,
  hasTitle: PropTypes.bool,
  hasButtons: PropTypes.bool,
  className: PropTypes.string,
};

/**
 * DashboardSkeleton - Skeleton para vistas tipo dashboard
 */
export const DashboardSkeleton = ({ className = "" }) => {
  return (
    <div className={`loading-skeleton-dashboard ${className}`}>
      {/* Header con estadísticas */}
      <div className="loading-skeleton-dashboard__stats">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="loading-skeleton-dashboard__stat-card">
            <Skeleton variant="text" width="60%" height="16px" />
            <Skeleton variant="text" width="80%" height="32px" />
          </div>
        ))}
      </div>

      {/* Contenido principal */}
      <div className="loading-skeleton-dashboard__content">
        <div className="loading-skeleton-dashboard__main">
          <Skeleton variant="rectangular" width="100%" height="300px" />
        </div>
        <div className="loading-skeleton-dashboard__sidebar">
          <Skeleton variant="rectangular" width="100%" height="200px" />
          <Skeleton variant="rectangular" width="100%" height="150px" />
        </div>
      </div>
    </div>
  );
};

DashboardSkeleton.propTypes = {
  className: PropTypes.string,
};

/**
 * ProfileSkeleton - Skeleton para perfiles de usuario
 */
export const ProfileSkeleton = ({ className = "" }) => {
  return (
    <div className={`loading-skeleton-profile ${className}`}>
      <div className="loading-skeleton-profile__header">
        <Skeleton variant="circular" width="100px" height="100px" />
        <div className="loading-skeleton-profile__info">
          <Skeleton variant="text" width="200px" height="24px" />
          <Skeleton variant="text" width="150px" height="16px" />
        </div>
      </div>

      <div className="loading-skeleton-profile__content">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="loading-skeleton-profile__field">
            <Skeleton variant="text" width="120px" height="14px" />
            <Skeleton variant="text" width="70%" height="18px" />
          </div>
        ))}
      </div>
    </div>
  );
};

ProfileSkeleton.propTypes = {
  className: PropTypes.string,
};
